/**
* Responsibility:
* - Escape input name values for safe query selector usage.
*
* Touches:
* - Used by input lookup helpers before `querySelectorAll`.
*
* Guarantees:
* - Returns escaped selector fragment for attribute matching.
*/
function escapeSelectorValue(value) {
	return String(value).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

/**
* Responsibility:
* - Resolve all form controls by exact `name` attribute.
*
* Touches:
* - Reads form DOM via `querySelectorAll([name="..."])`.
*
* Guarantees:
* - Returns array of matched elements.
*/
export function getElementsByInputName(form, name) {
	const safeName = escapeSelectorValue(name);
	return Array.from(form.querySelectorAll(`[name="${safeName}"]`));
}

/**
* Responsibility:
* - Read normalized input value for a GF field id by trying known input names.
*
* Touches:
* - Reads radio/checkbox/select/input controls under the form.
*
* Guarantees:
* - Returns scalar string, string array, empty string, or `null` when missing.
*/
export function readInputValue(form, inputId) {
	const baseNames = [
		`input_${inputId}`,
		`input_${String(inputId).replace('.', '_')}`,
	];
	const names = baseNames.flatMap((baseName) => [baseName, `${baseName}[]`]);

	for (const name of names) {
		const elements = getElementsByInputName(form, name);
		if (elements.length === 0) {
			continue;
		}

		const first = elements[0];
		if (first instanceof HTMLInputElement && first.type === 'radio') {
			const checked = elements.find((element) => element instanceof HTMLInputElement && element.checked);
			return checked instanceof HTMLInputElement ? checked.value : '';
		}

		if (first instanceof HTMLInputElement && first.type === 'checkbox') {
			if (elements.length === 1) {
				return first.checked ? (first.value || '1') : '';
			}

			return elements
				.filter((element) => element instanceof HTMLInputElement && element.checked)
				.map((element) => element.value || '1');
		}

		if (first instanceof HTMLSelectElement && first.multiple) {
			return Array.from(first.selectedOptions).map((option) => option.value);
		}

		if ('value' in first) {
			return first.value;
		}
	}

	return null;
}

/**
* Responsibility:
* - Read mapped hidden field value by logical key from runtime hidden map.
*
* Touches:
* - Resolves mapped field id then reads value through `readInputValue()`.
*
* Guarantees:
* - Returns empty string when mapping/value is unavailable.
*/
export function readHiddenValue(form, hiddenFieldMap, key) {
	const fieldId = hiddenFieldMap?.[key];
	if (!fieldId) {
		return '';
	}

	const value = readInputValue(form, fieldId);
	if (Array.isArray(value)) {
		return value[0] || '';
	}

	return value || '';
}

/**
* Responsibility:
* - Write mapped hidden field value by logical key to all matching controls.
*
* Touches:
* - Resolves mapped field id and mutates target input values.
*
* Guarantees:
* - No-op when mapped field controls cannot be found.
*/
export function writeHiddenValue(form, hiddenFieldMap, key, value) {
	const fieldId = hiddenFieldMap?.[key];
	if (!fieldId) {
		return;
	}

	const names = [
		`input_${fieldId}`,
		`input_${String(fieldId).replace('.', '_')}`,
	];

	for (const name of names) {
		const elements = getElementsByInputName(form, name);
		if (elements.length === 0) {
			continue;
		}

		elements.forEach((element) => {
			if ('value' in element) {
				element.value = value == null ? '' : String(value);
			}
		});
		return;
	}
}
